// /termin — Infogespräch-Buchungsseite für Webinar-Teilnehmer.
//
// Dominik nennt diese URL live während des Webinars ("Geht jetzt auf
// app-academy.ai/termin und sichert euch einen Slot"). Die Seite zeigt
// das Calendly-Inline-Widget mit einem dedizierten Event-Type, sodass
// Buchungen über den Webinar-Funnel separat von den /-page Infogesprächen
// nachverfolgt werden können (Calendly-Side: eigener Event-Type, App-Side:
// dieselbe trackContact-Pipeline via initCalendlyListener in main.jsx).
//
// Tracking: der globale Calendly postMessage-Listener feuert beim Booking
// automatisch Meta Lead + Contact, GA4 generate_lead + book_meeting und die
// Google-Ads-Conversion. Kein zusätzlicher Tracking-Code nötig.
//
// noindex by default — wir wollen die Infogespräch-Seite nicht im
// Index, sie hat keinen organischen Mehrwert.

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { CookieBanner, AAConsent, loadCalendly } from './CookieBanner.jsx';
import { WEBINAR_STRATEGY_CALL_URL } from '../lib/webinar.js';
import { updateGoogleConsent } from '../lib/google-ads.js';
import { enableMetaPixel } from '../lib/meta-pixel.js';
import { enableAnalytics } from '../lib/analytics.js';

// Pre-flight consent on module load — only when actually on /termin
// (main.jsx imports every route module at app boot, so the path guard
// keeps this from running on the home page and silently auto-granting
// consent there).
if (
  typeof window !== 'undefined' &&
  /^\/termin\/?$/i.test(window.location.pathname)
) {
  try {
    const current = AAConsent.current();
    if (!current.functional || !current.statistics || !current.marketing) {
      AAConsent.set({ functional: true, statistics: true, marketing: true });
    }
    updateGoogleConsent({ statistics: true, marketing: true });
    enableMetaPixel();
    enableAnalytics();
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[termin] consent prime failed:', e);
  }
}

function useTerminHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'AI App Academy · Infogespräch buchen';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        'Sichere dir ein 1:1-Infogespräch mit Dominik Groß im Anschluss an das AI App Academy Webinar.'
      );
    }
    let robots = document.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute('content', prevDesc);
      if (hadRobots && prevRobots != null) robots.setAttribute('content', prevRobots);
      else if (!hadRobots && robots) robots.remove();
    };
  }, []);
}

function CalendlyInline() {
  const containerRef = React.useRef(null);
  const [loadState, setLoadState] = React.useState('loading');

  React.useEffect(() => {
    let cancelled = false;
    loadCalendly()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        if (window.Calendly && typeof window.Calendly.initInlineWidget === 'function') {
          window.Calendly.initInlineWidget({
            url: WEBINAR_STRATEGY_CALL_URL,
            parentElement: containerRef.current,
          });
          setLoadState('ready');
        } else {
          setLoadState('error');
        }
      })
      .catch(() => { if (!cancelled) setLoadState('error'); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <div className="termin-calendly-fallback">
        <span className="termin-calendly-fallback-hint">Kalender lädt nicht?</span>
        <a
          href={WEBINAR_STRATEGY_CALL_URL}
          target="_blank"
          rel="noreferrer"
          className="btn btn-ghost termin-calendly-fallback-btn"
        >
          <span>Direkt auf Calendly öffnen</span>
          <Icon.ArrowRight size={16} />
        </a>
      </div>
      <div className="termin-calendly-wrap">
        {loadState === 'loading' && (
          <div className="termin-calendly-state" role="status">
            <span className="termin-calendly-spinner" aria-hidden="true" />
            <span>Buchungskalender wird geladen…</span>
          </div>
        )}
        {loadState === 'error' && (
          <div className="termin-calendly-state termin-calendly-state-error">
            <Icon.AlertTriangle size={18} />
            <span>
              Der Kalender konnte nicht geladen werden. Öffne ihn direkt:{' '}
              <a href={WEBINAR_STRATEGY_CALL_URL} target="_blank" rel="noreferrer">
                {WEBINAR_STRATEGY_CALL_URL.replace('https://', '')}
              </a>
            </span>
          </div>
        )}
        <div ref={containerRef} className="termin-calendly-target" />
      </div>
    </>
  );
}

export default function WebinarTerminPage() {
  useTerminHead();

  return (
    <>
      <nav className="nav nav-webinar">
        <div className="nav-inner">
          <a href="/" className="logo">
            <div className="logo-mark"><LogoMark size={32} /></div>
            <span className="logo-text">AI App Academy</span>
          </a>
        </div>
      </nav>

      <main className="hero webinar-hero termin-hero" id="top">
        <div className="hero-bg-grid" aria-hidden="true"></div>
        <div className="container">
          <div className="webinar-eyebrow">
            <span className="webinar-live-dot"></span>
            <span style={{ textTransform: 'none' }}>1:1 INFOGESPRÄCH MIT DOMINIK</span>
          </div>

          <h1 className="hero-headline webinar-headline termin-headline">
            Sichere dir jetzt dein <em>Infogespräch</em>.
          </h1>

          <p className="webinar-sub">
            Direkt im Anschluss an das Webinar gehen wir 1:1 auf dein App-Vorhaben ein. Wir
            besprechen deine Idee, deinen Use Case und prüfen gemeinsam, ob die App Academy
            die richtige Begleitung für dich ist.
          </p>

          <CalendlyInline />
        </div>
      </main>

      <footer className="webinar-footer">
        <div className="container">
          <div className="webinar-footer-row">
            <div className="webinar-footer-brand">
              <div className="logo-mark"><LogoMark size={24} /></div>
              <span>© 2026 Incibit GmbH · App Academy</span>
            </div>
            <div className="webinar-footer-links">
              <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
              <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
              <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
              <a
                href="#cookie-settings"
                onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
              >
                Cookie-Einstellungen
              </a>
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </>
  );
}
